import { useEffect, useRef, useState } from "react";
import { signInAnonymously } from "firebase/auth";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { auth, storage } from "../../firebase";

const BRAND = {
  navy: "#1d52a0",
  navyDark: "#0d1833",
  gold: "#fcb31f",
  pink: "#ef537e",
  cream: "#ffdaad",
  green: "#70be44",
  line: "rgba(255,255,255,0.2)",
  muted: "rgba(255,218,173,0.7)",
};

const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.85;

// Compress client-side before upload. Reuses the pattern from existing
// customer flows: 1920px max edge, 85% JPEG.
async function compressImage(file) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY);
  });
}

function SlotRow({ idx, slot, onPick, onNotesChange, onClear, disabled }) {
  const inputRef = useRef();
  const previewUrl = slot.file ? slot.previewUrl : slot.uploadedUrl;

  return (
    <div style={{
      background: "rgba(255,255,255,0.06)",
      border: `1px solid ${BRAND.line}`,
      borderRadius: "12px",
      padding: "0.9rem",
      marginBottom: "0.75rem",
    }}>
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.8rem", marginBottom: "0.5rem", letterSpacing: "0.06em" }}>
        IMAGE {idx + 1}
      </div>

      {previewUrl ? (
        <div style={{ position: "relative", marginBottom: "0.6rem" }}>
          <img
            src={previewUrl}
            alt={`Submission ${idx + 1}`}
            style={{
              display: "block",
              width: "100%",
              maxHeight: "280px",
              objectFit: "contain",
              background: "#000",
              borderRadius: "8px",
            }}
          />
          {!disabled && !slot.uploadedUrl && (
            <button
              type="button"
              onClick={() => onClear(idx)}
              style={{
                position: "absolute",
                top: "0.4rem",
                right: "0.4rem",
                background: "rgba(0,0,0,0.6)",
                color: "white",
                border: "none",
                borderRadius: "999px",
                padding: "0.3rem 0.6rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Remove
            </button>
          )}
          {slot.uploadedUrl && (
            <div style={{
              position: "absolute",
              top: "0.4rem",
              right: "0.4rem",
              background: BRAND.green,
              color: "white",
              borderRadius: "999px",
              padding: "0.25rem 0.6rem",
              fontSize: "0.72rem",
              fontWeight: 800,
            }}>
              ✓ Sent
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          style={{
            width: "100%",
            background: "rgba(252,179,31,0.12)",
            border: `2px dashed ${BRAND.gold}`,
            color: BRAND.gold,
            borderRadius: "8px",
            padding: "1.6rem 1rem",
            fontSize: "0.95rem",
            fontWeight: 700,
            cursor: disabled ? "default" : "pointer",
            marginBottom: "0.6rem",
          }}
        >
          📷 Tap to choose an image
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(idx, f);
          e.target.value = ""; // allow re-picking the same file
        }}
      />

      <textarea
        value={slot.notes}
        onChange={(e) => onNotesChange(idx, e.target.value)}
        placeholder="Notes (optional) — e.g. 'this is our logo', 'use these colors', 'guests love this'"
        rows={2}
        disabled={disabled || !!slot.uploadedUrl}
        style={{
          width: "100%",
          background: "rgba(0,0,0,0.25)",
          border: `1px solid ${BRAND.line}`,
          borderRadius: "8px",
          color: BRAND.cream,
          padding: "0.55rem 0.7rem",
          fontSize: "0.9rem",
          fontFamily: "inherit",
          resize: "vertical",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

export default function FlashBoardSubmit({ token }) {
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [slots, setSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [doneFlash, setDoneFlash] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(getApp()), "getFlashBookingByToken");
        const res = await fn({ token });
        if (cancelled) return;
        setBooking(res.data);
        setSlots(Array.from({ length: res.data.maxSlots }, () => ({
          file: null, previewUrl: null, notes: "", uploadedUrl: null, uploading: false,
        })));
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Invalid link.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onPick = (idx, file) => {
    const url = URL.createObjectURL(file);
    setSlots((s) => s.map((slot, i) => i === idx ? { ...slot, file, previewUrl: url, uploadedUrl: null } : slot));
  };
  const onClear = (idx) => {
    setSlots((s) => s.map((slot, i) => {
      if (i !== idx) return slot;
      if (slot.previewUrl) URL.revokeObjectURL(slot.previewUrl);
      return { ...slot, file: null, previewUrl: null, uploadedUrl: null };
    }));
  };
  const onNotesChange = (idx, value) => {
    setSlots((s) => s.map((slot, i) => i === idx ? { ...slot, notes: value } : slot));
  };

  const pendingCount = slots.filter((s) => s.file && !s.uploadedUrl).length;

  const onSubmit = async () => {
    if (submitting || pendingCount === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Anonymous auth for Storage uploads
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      // Upload each pending image sequentially — predictable progress, small count.
      const uploaded = [];
      const updatedSlots = [...slots];
      for (let i = 0; i < updatedSlots.length; i++) {
        const s = updatedSlots[i];
        if (!s.file || s.uploadedUrl) continue;
        const blob = await compressImage(s.file);
        const ts = Date.now();
        const rand = Math.random().toString(36).slice(2, 8);
        const path = `flash-board-submissions/${booking.bookingId}/${ts}_${rand}.jpg`;
        const r = storageRef(storage, path);
        await uploadBytes(r, blob, { contentType: "image/jpeg" });
        const imageUrl = await getDownloadURL(r);
        updatedSlots[i] = { ...s, uploadedUrl: imageUrl };
        uploaded.push({ imageUrl, notes: s.notes.trim() || null });
        setSlots([...updatedSlots]);
      }

      if (uploaded.length === 0) {
        throw new Error("Nothing to upload.");
      }

      const fn = httpsCallable(getFunctions(getApp()), "submitFlashBoardImages");
      await fn({ token, submissions: uploaded });

      // Mark success; clear file refs (but keep uploadedUrl for the ✓ UI)
      setDoneFlash(true);
      setTimeout(() => setDoneFlash(false), 2500);
      setSlots((s) => s.map((slot) => slot.uploadedUrl ? { ...slot, file: null, previewUrl: null } : slot));
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background: BRAND.navyDark, minHeight: "100vh", color: BRAND.cream, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--jop-display)" }}>
        Loading…
      </div>
    );
  }

  if (loadError || !booking) {
    return (
      <div style={{ background: BRAND.navyDark, minHeight: "100vh", color: BRAND.cream, fontFamily: "var(--jop-display)", padding: "2rem", boxSizing: "border-box" }}>
        <div style={{ maxWidth: "420px", margin: "3rem auto", textAlign: "center" }}>
          <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ width: "240px", maxWidth: "100%", marginBottom: "1.5rem" }} />
          <h1 style={{ color: BRAND.pink, fontSize: "1.3rem", margin: "0 0 0.8rem" }}>Link not active</h1>
          <p style={{ color: BRAND.muted, lineHeight: 1.6 }}>{loadError || "This link has expired or was never valid. Please contact Dan directly."}</p>
        </div>
      </div>
    );
  }

  const title = booking.eventName ? `Flash board for ${booking.eventName}` : "Flash board concepts";

  return (
    <div style={{
      background: BRAND.navyDark,
      minHeight: "100vh",
      color: BRAND.cream,
      fontFamily: "var(--jop-display)",
      padding: "1.5rem 1rem 3rem",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "540px", margin: "0 auto" }}>

        <div style={{ textAlign: "center", marginBottom: "1.2rem" }}>
          <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ width: "220px", maxWidth: "100%" }} />
        </div>

        <div style={{
          background: BRAND.navy,
          borderRadius: "16px",
          padding: "1.1rem 1.2rem",
          marginBottom: "1rem",
          border: `1px solid ${BRAND.line}`,
        }}>
          <div style={{ color: BRAND.gold, fontWeight: 900, fontSize: "0.9rem", letterSpacing: "2px", marginBottom: "0.3rem" }}>
            FLASH BOARD CONCEPTS
          </div>
          <h1 style={{ margin: 0, fontSize: "1.25rem", color: "white" }}>
            {title}
          </h1>
          {booking.existingCount > 0 && (
            <p style={{ margin: "0.5rem 0 0", color: BRAND.muted, fontSize: "0.85rem" }}>
              {booking.existingCount} image{booking.existingCount === 1 ? "" : "s"} already sent — you can add more below.
            </p>
          )}
        </div>

        <p style={{ color: BRAND.muted, fontSize: "0.92rem", lineHeight: 1.6, margin: "0 0 1rem" }}>
          Share up to <strong style={{ color: BRAND.cream }}>{booking.maxSlots}</strong> images that help me dial in the vibe — logos, mascots, references, anything you love. Notes are optional. Come back to this link anytime to add more.
        </p>

        {slots.map((slot, i) => (
          <SlotRow
            key={i}
            idx={i}
            slot={slot}
            onPick={onPick}
            onNotesChange={onNotesChange}
            onClear={onClear}
            disabled={submitting}
          />
        ))}

        {submitError && (
          <div style={{
            background: BRAND.pink, color: "white",
            padding: "0.7rem 0.9rem", borderRadius: "8px",
            marginTop: "0.5rem", fontSize: "0.9rem",
          }}>
            {submitError}
          </div>
        )}

        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || pendingCount === 0}
          style={{
            marginTop: "1rem",
            width: "100%",
            background: (submitting || pendingCount === 0) ? "rgba(255,255,255,0.15)" : BRAND.gold,
            color: (submitting || pendingCount === 0) ? BRAND.muted : "#1a0a12",
            border: "none",
            borderRadius: "12px",
            padding: "1rem",
            fontSize: "1.05rem",
            fontWeight: 900,
            cursor: (submitting || pendingCount === 0) ? "default" : "pointer",
            letterSpacing: "0.02em",
          }}
        >
          {submitting ? "Sending…" : doneFlash ? "✓ Sent! Thank you." : pendingCount === 0 ? "Choose an image to send" : `Send ${pendingCount} image${pendingCount === 1 ? "" : "s"}`}
        </button>

        <p style={{ color: "rgba(255,218,173,0.5)", fontSize: "0.78rem", textAlign: "center", marginTop: "1.5rem" }}>
          The Joy of Pancakes · thejoyofpancakes.com
        </p>
      </div>
    </div>
  );
}
